import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { getErrorMessage } from '@/shared/data/errors';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, UserDto, UserListItemDto } from '@flowqa/shared';

type SuperuserBlockInput = { id: string; blocked: boolean; reason?: string | null };
type SuperuserUpdateInput = {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
};
type SuperuserDeleteResult = { status: 'deleted' };
type SuperuserRevokeSessionsResult = { status: 'revoked' };

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (tokenOverride?: string | null): string => {
  const token = tokenOverride ?? getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

export const querySuperuserUsers = async (
  input: GridQueryInput,
  tokenOverride?: string | null,
): Promise<QueryResult<UserListItemDto>> => {
  const authToken = ensureToken(tokenOverride);
  try {
    return await apiClient.post<QueryResult<UserListItemDto>>(
      'superuser/users/query',
      { page: input.page, pageSize: input.pageSize, sorts: input.sorts, filters: input.filters },
      { retries: 1, token: authToken },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const setSuperuserUserBlocked = async (
  input: SuperuserBlockInput,
  tokenOverride?: string | null,
): Promise<UserDto> => {
  const authToken = ensureToken(tokenOverride);
  try {
    return await apiClient.post<UserDto>(
      `superuser/users/${input.id}/block`,
      { blocked: input.blocked, reason: input.reason ?? null },
      { retries: 1, token: authToken },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateSuperuserUser = async (
  input: SuperuserUpdateInput,
  tokenOverride?: string | null,
): Promise<UserDto> => {
  const authToken = ensureToken(tokenOverride);
  try {
    return await apiClient.patch<UserDto>(
      `users/${input.id}`,
      {
        email: input.email,
        firstName: input.firstName ?? null,
        lastName: input.lastName ?? null,
      },
      { retries: 1, token: authToken },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const deleteSuperuserUser = async (
  input: { id: string },
  tokenOverride?: string | null,
): Promise<SuperuserDeleteResult> => {
  const authToken = ensureToken(tokenOverride);
  try {
    return await apiClient.post<SuperuserDeleteResult>(
      `superuser/users/${input.id}/delete`,
      {},
      { retries: 1, token: authToken },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const revokeSuperuserUserSessions = async (
  input: { id: string },
  tokenOverride?: string | null,
): Promise<SuperuserRevokeSessionsResult> => {
  const authToken = ensureToken(tokenOverride);
  try {
    return await apiClient.post<SuperuserRevokeSessionsResult>(
      `superuser/users/${input.id}/revoke-sessions`,
      {},
      { retries: 1, token: authToken },
    );
  } catch (err) {
    throw toApiError(err);
  }
};
